import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, LogIn } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Captcha from "@/components/Captcha";

export default function Login() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!captchaToken) { toast.error("Please complete the captcha"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", {
        email: email.trim(),
        password,
        captcha_token: captchaToken,
      });
      toast.success("Welcome back!");
      await refresh();
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err));
      setCaptchaToken("");
    } finally { setBusy(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-10" data-testid="login-page">
      <div className="w-full max-w-md glass-strong rounded-3xl p-8 sm:p-10">
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl font-black tracking-tight">Welcome back</h1>
          <p className="text-zinc-400 text-sm mt-2">Login to like videos and join the community.</p>
        </div>

        <button
          data-testid="google-login-btn"
          onClick={googleLogin}
          className="w-full flex items-center justify-center gap-3 py-3 rounded-xl bg-white text-black font-bold hover:bg-zinc-100 transition-colors mb-4"
        >
          <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.5 12.25c0-.79-.07-1.54-.2-2.27H12v4.3h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.09-1.93 3.29-4.77 3.29-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.28-1.93-6.15-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.85 14.12A6.6 6.6 0 0 1 5.5 12c0-.74.13-1.46.35-2.12V7.04H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.96l3.67-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.46 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.67 2.84C6.72 7.3 9.14 5.38 12 5.38z"/></svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-3 my-5 text-xs text-zinc-500">
          <div className="flex-1 h-px bg-white/10" /> OR <div className="flex-1 h-px bg-white/10" />
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              data-testid="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-[#E50914]/50 text-sm outline-none"
            />
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              data-testid="login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-[#E50914]/50 text-sm outline-none"
            />
          </div>
          <div className="text-right">
            <Link to="/forgot-password" data-testid="goto-forgot" className="text-xs text-zinc-400 hover:text-[#E50914] font-semibold">
              Forgot password?
            </Link>
          </div>
          <Captcha onVerify={setCaptchaToken} onExpire={() => setCaptchaToken("")} />
          <button
            data-testid="login-submit"
            type="submit"
            disabled={busy || !captchaToken}
            className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-[#E50914] hover:bg-[#F6121D] text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <LogIn className="w-4 h-4" /> {busy ? "Signing in..." : "Login"}
          </button>
        </form>
        <p className="text-sm text-zinc-400 text-center mt-6">
          New here?{" "}
          <Link to="/register" data-testid="goto-register" className="text-[#E50914] font-semibold hover:underline">
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
}
